import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Card, Button, Input, Modal } from '@components/common/UIComponents';
import {
  addWeightEntry,
  getWeightHistory,
  deleteWeightEntry,
  getUserSettings,
  updateUserSettings
} from '@services/firestoreService';
import { 
  calculateBMI, 
  getBMICategory, 
  calculateDaysToGoal,
  getWeeklyWeightTarget,
  calculateWeeklyChange,
  checkWeeklyTarget,
  getWeekStart
} from '@utils/helpers';
import { Weight as WeightIcon, TrendingUp, TrendingDown, Target, Calendar, CheckCircle, AlertCircle, Award } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { format, startOfWeek, endOfWeek } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const WeightPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [weightHistory, setWeightHistory] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  
  const [settings, setSettings] = useState({
    height: 170,
    targetWeight: null,
    weightGoal: 'Lose'
  });

  const [formData, setFormData] = useState({
    weight: '',
    notes: ''
  });

  const [goalWeight, setGoalWeight] = useState('');

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const [history, userSettings] = await Promise.all([
        getWeightHistory(user.uid, 90),
        getUserSettings(user.uid)
      ]);
      
      setWeightHistory(history);
      
      if (userSettings) {
        setSettings({
          height: userSettings.height || 170,
          targetWeight: userSettings.targetWeight || null,
          weightGoal: userSettings.weightGoal || 'Lose'
        });
        setGoalWeight(userSettings.targetWeight || '');
      }
    } catch (error) {
      console.error('Error loading weight data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddWeight = async () => {
    if (!formData.weight || formData.weight <= 0) return;

    try {
      await addWeightEntry(user.uid, parseFloat(formData.weight), formData.notes);
      setShowAddModal(false);
      setFormData({ weight: '', notes: '' });
      loadData();
    } catch (error) {
      console.error('Error adding weight:', error);
    }
  };

  const handleSaveGoal = async () => {
    if (!goalWeight || goalWeight <= 0) return;

    try {
      const userSettings = await getUserSettings(user.uid);
      const updatedSettings = {
        ...(userSettings || {}),
        targetWeight: parseFloat(goalWeight),
        height: settings.height
      };
      await updateUserSettings(user.uid, updatedSettings);
      setShowGoalModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving goal:', error);
    }
  };

  const currentWeight = weightHistory.length > 0 ? weightHistory[0].weight : null;
  const previousWeight = weightHistory.length > 1 ? weightHistory[1].weight : null;
  const weightChange = currentWeight && previousWeight ? currentWeight - previousWeight : 0;

  // Calculate BMI
  const bmi = currentWeight ? calculateBMI(currentWeight, settings.height) : null;
  const bmiCategory = bmi ? getBMICategory(bmi) : null;

  // Days to goal
  const daysToGoal = currentWeight && settings.targetWeight 
    ? calculateDaysToGoal(currentWeight, settings.targetWeight)
    : null;

  // Weekly tracking calculations
  const weeklyTarget = getWeeklyWeightTarget(settings.weightGoal);
  const weeklyData = calculateWeeklyChange(weightHistory);
  const weeklyStatus = weeklyData.weeklyChange !== 0 
    ? checkWeeklyTarget(weeklyData.weeklyChange, weeklyTarget)
    : null;
  const weekStart = getWeekStart();
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  // Prepare chart data
  const chartData = {
    labels: weightHistory.slice().reverse().map(item => format(new Date(item.date), 'MMM dd')),
    datasets: [
      {
        label: 'Weight (kg)',
        data: weightHistory.slice().reverse().map(item => item.weight),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        tension: 0.4,
        fill: true
      },
      ...(settings.targetWeight ? [{
        label: 'Target',
        data: Array(weightHistory.length).fill(settings.targetWeight),
        borderColor: 'rgb(239, 68, 68)',
        borderDash: [5, 5],
        pointRadius: 0,
        tension: 0
      }] : [])
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true
      }
    },
    scales: {
      y: {
        beginAtZero: false
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Weight Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track your weight progress over time
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <WeightIcon size={20} className="mr-2" />
          Log Weight
        </Button>
      </div>

      {/* Current Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center gap-3 mb-2">
            <WeightIcon className="text-purple-600 dark:text-purple-400" size={24} />
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Current Weight</p>
          </div>
          <p className="text-4xl font-bold text-gray-900 dark:text-gray-100">
            {currentWeight ? `${currentWeight} kg` : '--'}
          </p>
          {weightChange !== 0 && (
            <div className="flex items-center gap-1 mt-2">
              {weightChange > 0 ? (
                <TrendingUp size={16} className="text-red-600" />
              ) : (
                <TrendingDown size={16} className="text-green-600" />
              )}
              <p className={`text-sm font-medium ${weightChange > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {Math.abs(weightChange).toFixed(1)} kg
              </p>
            </div>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-3 mb-2">
            <Target className="text-blue-600 dark:text-blue-400" size={24} />
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Target Weight</p>
          </div>
          <p className="text-4xl font-bold text-gray-900 dark:text-gray-100">
            {settings.targetWeight ? `${settings.targetWeight} kg` : '--'}
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowGoalModal(true)}
            className="mt-2"
          >
            Set Goal
          </Button>
        </Card>

        <Card>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-6 h-6 bg-green-100 dark:bg-green-900/20 rounded flex items-center justify-center">
              <span className="text-green-600 dark:text-green-400 text-xs font-bold">BMI</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Body Mass Index</p>
          </div>
          <p className="text-4xl font-bold text-gray-900 dark:text-gray-100">
            {bmi ? bmi.toFixed(1) : '--'}
          </p>
          {bmiCategory && (
            <p className={`text-sm font-medium mt-2 ${bmiCategory.color}`}>
              {bmiCategory.category}
            </p>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="text-orange-600 dark:text-orange-400" size={24} />
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Days to Goal</p>
          </div>
          <p className="text-4xl font-bold text-gray-900 dark:text-gray-100">
            {daysToGoal !== null ? daysToGoal : '--'}
          </p>
          {daysToGoal && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              ~{Math.round(daysToGoal / 7)} weeks
            </p>
          )}
        </Card>
      </div>

      {/* Weekly Weight Tracking */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
        <div className="flex items-center gap-3 mb-4">
          <Award className="text-blue-600 dark:text-blue-400" size={28} />
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Weekly Weight Tracking
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {format(weekStart, 'MMM dd')} - {format(weekEnd, 'MMM dd, yyyy')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          {/* Weekly Target */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="text-blue-600 dark:text-blue-400" size={20} />
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Weekly Target</p>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {weeklyTarget > 0 ? '+' : ''}{weeklyTarget} kg
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {settings.weightGoal === 'Lose' && 'Healthy weight loss rate'}
              {settings.weightGoal === 'Gain' && 'Healthy weight gain rate'}
              {settings.weightGoal === 'Maintain' && 'Maintain current weight'}
            </p>
          </div>

          {/* Actual Change */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="text-purple-600 dark:text-purple-400" size={20} />
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Actual Change</p>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {weeklyData.weeklyChange !== 0 
                ? `${weeklyData.weeklyChange > 0 ? '+' : ''}${weeklyData.weeklyChange.toFixed(2)} kg`
                : '--'}
            </p>
            {weeklyData.startWeight && weeklyData.endWeight && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {weeklyData.startWeight.toFixed(1)} → {weeklyData.endWeight.toFixed(1)} kg
              </p>
            )}
          </div>

          {/* Status */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              {weeklyStatus?.achieved ? (
                <CheckCircle className="text-green-600 dark:text-green-400" size={20} />
              ) : (
                <AlertCircle className="text-orange-600 dark:text-orange-400" size={20} />
              )}
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</p>
            </div>
            {weeklyStatus ? (
              <>
                <p className={`text-lg font-bold ${
                  weeklyStatus.status === 'success' ? 'text-green-600 dark:text-green-400' :
                  weeklyStatus.status === 'warning' ? 'text-orange-600 dark:text-orange-400' :
                  weeklyStatus.status === 'caution' ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-red-600 dark:text-red-400'
                }`}>
                  {weeklyStatus.achieved ? '✓ On Track' : '✗ Off Track'}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {weeklyStatus.message}
                </p>
              </>
            ) : (
              <>
                <p className="text-lg font-bold text-gray-500 dark:text-gray-400">
                  Not enough data
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Log weight weekly to track progress
                </p>
              </>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        {weeklyStatus && (
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600 dark:text-gray-400">Progress</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {Math.abs(weeklyData.weeklyChange / weeklyTarget * 100).toFixed(0)}% of target
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div 
                className={`h-3 rounded-full transition-all ${
                  weeklyStatus.achieved 
                    ? 'bg-green-600' 
                    : 'bg-orange-600'
                }`}
                style={{ 
                  width: `${Math.min(Math.abs(weeklyData.weeklyChange / weeklyTarget * 100), 100)}%` 
                }}
              />
            </div>
          </div>
        )}

        {/* Recommendation */}
        <div className="mt-6 p-4 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
          <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
            💡 Recommendation
          </p>
          <p className="text-sm text-blue-800 dark:text-blue-200">
            {settings.weightGoal === 'Lose' && (
              <>
                Weigh yourself once per week, preferably on the same day and time (e.g., Sunday morning after waking up). 
                Aim to lose 0.5 kg per week for sustainable, healthy weight loss. Stay consistent with your {' '}
                <span className="font-semibold">calorie target</span> and regular exercise.
              </>
            )}
            {settings.weightGoal === 'Gain' && (
              <>
                Weigh yourself weekly at the same time. Aim for 0.3 kg gain per week for lean muscle growth. 
                Ensure you're eating enough protein and following a strength training routine.
              </>
            )}
            {settings.weightGoal === 'Maintain' && (
              <>
                Weigh yourself weekly to monitor. Small fluctuations (±0.5 kg) are normal due to water retention. 
                Focus on maintaining your current calorie intake and activity level.
              </>
            )}
          </p>
        </div>
      </Card>

      {/* Weight Chart */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Weight Progress
        </h3>
        <div className="h-80">
          {weightHistory.length > 0 ? (
            <Line data={chartData} options={chartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
              No weight data available
            </div>
          )}
        </div>
      </Card>

      {/* Weight History */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Weight History
        </h3>
        {weightHistory.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <WeightIcon size={48} className="mx-auto mb-3 opacity-30" />
            <p>No weight entries yet</p>
            <p className="text-sm mt-1">Start tracking by logging your first weight</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {weightHistory.map((entry, index) => {
              const prevEntry = weightHistory[index + 1];
              const change = prevEntry ? entry.weight - prevEntry.weight : 0;
              
              return (
                <div
                  key={entry.id}
                  className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4"
                >
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {entry.weight} kg
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {format(new Date(entry.date), 'MMMM dd, yyyy • h:mm a')}
                    </p>
                    {entry.notes && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 italic">
                        {entry.notes}
                      </p>
                    )}
                  </div>
                  {change !== 0 && (
                    <div className="flex items-center gap-2">
                      {change > 0 ? (
                        <TrendingUp size={18} className="text-red-600" />
                      ) : (
                        <TrendingDown size={18} className="text-green-600" />
                      )}
                      <span className={`text-sm font-medium ${change > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {change > 0 ? '+' : ''}{change.toFixed(1)} kg
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Add Weight Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Log Weight"
      >
        <div className="space-y-4">
          <Input
            type="number"
            label="Weight (kg)"
            placeholder="70.5"
            value={formData.weight}
            onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
            fullWidth
            step="0.1"
            required
          />
          <Input
            label="Notes (optional)"
            placeholder="Feeling great today!"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            fullWidth
          />
          <div className="flex gap-3">
            <Button onClick={handleAddWeight} fullWidth>
              Save
            </Button>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} fullWidth>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Set Goal Modal */}
      <Modal
        isOpen={showGoalModal}
        onClose={() => setShowGoalModal(false)}
        title="Set Target Weight"
      >
        <div className="space-y-4">
          <Input
            type="number"
            label="Target Weight (kg)"
            placeholder="65.0"
            value={goalWeight}
            onChange={(e) => setGoalWeight(e.target.value)}
            fullWidth
            step="0.1"
            required
          />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Based on a healthy weight loss rate of 0.5kg per week
          </p>
          <div className="flex gap-3">
            <Button onClick={handleSaveGoal} fullWidth>
              Save Goal
            </Button>
            <Button variant="secondary" onClick={() => setShowGoalModal(false)} fullWidth>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WeightPage;
